import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'
import { KeyScreen } from './components/key-screen'

const LivePage = lazy(() => import('./pages/live-page'))
const SignPage = lazy(() => import('./pages/sign-page'))
const HowPage = lazy(() => import('./pages/how-page'))

function Lazy({ children }: { children: React.ReactNode }) {
	return <Suspense fallback={<KeyScreen reason="loading" />}>{children}</Suspense>
}

const rootRoute = createRootRoute({ component: () => <Outlet /> })

// No key in the URL: explain what this is and where the key comes from.
const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	component: () => <KeyScreen reason="missing" />,
})

// The big-screen board. Any valid key may watch.
const liveRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/live/$token/',
	component: () => (
		<Lazy>
			<LivePage />
		</Lazy>
	),
})

// A player's personal signing link.
const signRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/s/$token/',
	component: () => (
		<Lazy>
			<SignPage />
		</Lazy>
	),
})

// How signing works and what the proof does and does not show. No key needed: nothing confidential.
const howRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/how-it-works/',
	component: () => (
		<Lazy>
			<HowPage />
		</Lazy>
	),
})

const notFoundRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '$',
	component: () => <KeyScreen reason="missing" />,
})

export const router = createRouter({
	routeTree: rootRoute.addChildren([indexRoute, liveRoute, signRoute, howRoute, notFoundRoute]),
	trailingSlash: 'always',
})

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}
